'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComboboxItem } from '@sola/ark-core/molecules/combobox-field/combobox-field.tsx';
import { useAuth } from '@sola/auth-sdk/store.tsx';
import { formatPostalCodeLabel } from '@sola/skippy-search-sdk/lib/geo.ts';
import { fetchAutocompletePostalCode } from '@sola/skippy-search-sdk/lib/queries.ts';
import type { GeoLocationDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import { VolunteerSortByEnum, usePaginationQueryParams, useVolunteerQueryParams } from '../../utils/query-params.tsx';
import type { ResolvedLocation } from './volunteer-controls.logic.ts';
interface UseVolunteerFilterOptions {
  limit?: number;
  defaultLocation?: ResolvedLocation | null;
  onGeoChange?: (geo?: GeoLocationDto) => void;
}
export const useVolunteerFilter = t0 => {
  const $ = _c(56);
  const {
    limit,
    defaultLocation,
    onGeoChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["volunteers"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    authUser
  } = useAuth("authUser");
  const userName = authUser?.supporter?.preferredName || authUser?.supporter?.firstName;
  let t2;
  if ($[1] !== t) {
    t2 = () => {
      if (globalThis.window !== undefined) {
        const urlQ = new URLSearchParams(globalThis.location.search).get("q");
        if (urlQ && urlQ !== "my-location") {
          return urlQ;
        }
        if (urlQ === "my-location") {
          return t("volunteers:myLocation");
        }
      }
      return "";
    };
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [locationInput, setLocationInput] = useState(t2);
  const userInteractedRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const prevSourceRef = useRef(null);
  let t3;
  let t4;
  if ($[3] !== defaultLocation || $[4] !== t) {
    t3 = () => {
      if (userInteractedRef.current) {
        return;
      }
      if (!defaultLocation) {
        return;
      }
      const isNewSource = defaultLocation.source !== prevSourceRef.current;
      if (hasInitializedRef.current && !isNewSource) {
        return;
      }
      hasInitializedRef.current = true;
      prevSourceRef.current = defaultLocation.source;
      const target = defaultLocation.source === "browser" ? t("volunteers:myLocation") : defaultLocation.display;
      setLocationInput(target ?? "");
    };
    t4 = [defaultLocation, t];
    $[3] = defaultLocation;
    $[4] = t;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== t) {
    t5 = t("volunteers:sort.relevance");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = {
      label: t5,
      value: VolunteerSortByEnum.enum.relevance
    };
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t("volunteers:sort.location");
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = {
      label: t7,
      value: VolunteerSortByEnum.enum.location
    };
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t) {
    t9 = t("volunteers:sort.date");
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = {
      label: t9,
      value: VolunteerSortByEnum.enum.date
    };
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t6 || $[21] !== t8) {
    t11 = [t6, t8, t10];
    $[19] = t10;
    $[20] = t6;
    $[21] = t8;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const sortByOptions = t11;
  let t12;
  if ($[23] !== limit) {
    t12 = {
      perPage: limit
    };
    $[23] = limit;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const {
    volunteerFilters,
    setVolunteerFilters
  } = useVolunteerQueryParams(t12);
  const {
    setCurrentPage
  } = usePaginationQueryParams();
  const prevQRef = useRef(volunteerFilters.q);
  let t13;
  let t14;
  if ($[25] !== onGeoChange || $[26] !== t || $[27] !== volunteerFilters.q) {
    t13 = () => {
      const q = volunteerFilters.q;
      if (q === prevQRef.current) {
        return;
      }
      prevQRef.current = q;
      if (!q) {
        return;
      }
      if (q === "my-location") {
        setLocationInput(t("volunteers:myLocation"));
        return;
      }
      fetchAutocompletePostalCode(q, 1).then(results => {
        if (results.length > 0) {
          const r = results[0];
          setLocationInput(formatPostalCodeLabel(r));
          onGeoChange?.({
            city: r.city,
            state: r.state,
            latitude: r.lat,
            longitude: r.lng
          });
        } else {
          setLocationInput(q);
        }
      });
    };
    t14 = [volunteerFilters.q, t, onGeoChange];
    $[25] = onGeoChange;
    $[26] = t;
    $[27] = volunteerFilters.q;
    $[28] = t13;
    $[29] = t14;
  } else {
    t13 = $[28];
    t14 = $[29];
  }
  useEffect(t13, t14);
  let t15;
  if ($[30] !== sortByOptions || $[31] !== t || $[32] !== volunteerFilters.sortby) {
    t15 = sortByOptions.find(o => o.value === volunteerFilters.sortby)?.label ?? t("volunteers:sort.relevance");
    $[30] = sortByOptions;
    $[31] = t;
    $[32] = volunteerFilters.sortby;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const sortLabel = t15;
  let t16;
  if ($[34] !== t) {
    t16 = t("volunteers:sortByPrefix");
    $[34] = t;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const sortInput = `${t16}: ${sortLabel}`;
  let t17;
  if ($[36] !== setVolunteerFilters) {
    t17 = item => {
      setVolunteerFilters(prev => ({
        ...prev,
        sortby: item.value as VolunteerSortByEnum
      }));
    };
    $[36] = setVolunteerFilters;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const handleSortSelect = t17;
  let t18;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = val => {
      userInteractedRef.current = true;
      setLocationInput(val);
    };
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const handleLocationValueChange = t18;
  let t19;
  if ($[39] !== onGeoChange) {
    t19 = geo => {
      if (geo?.latitude && geo?.longitude) {
        onGeoChange?.(geo);
      } else {
        onGeoChange?.();
      }
    };
    $[39] = onGeoChange;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const handleGeoChange = t19;
  let t20;
  if ($[41] !== setCurrentPage || $[42] !== setVolunteerFilters) {
    t20 = val_0 => {
      setVolunteerFilters(prev_0 => ({
        ...prev_0,
        q: val_0 || null
      }));
      setCurrentPage(0);
    };
    $[41] = setCurrentPage;
    $[42] = setVolunteerFilters;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const handleLocationSubmit = t20;
  let t21;
  if ($[44] !== t || $[45] !== userName) {
    t21 = userName ? t("volunteers:welcomeBack", {
      name: userName
    }) : t("volunteers:upcomingEvents");
    $[44] = t;
    $[45] = userName;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const listTitle = t21;
  let t22;
  if ($[47] !== handleGeoChange || $[48] !== handleLocationSubmit || $[49] !== handleSortSelect || $[50] !== listTitle || $[51] !== locationInput || $[52] !== sortByOptions || $[53] !== sortInput || $[54] !== t) {
    t22 = {
      t,
      locationInput,
      sortInput,
      sortByOptions,
      listTitle,
      handleSortSelect,
      handleLocationValueChange,
      handleGeoChange,
      handleLocationSubmit
    };
    $[47] = handleGeoChange;
    $[48] = handleLocationSubmit;
    $[49] = handleSortSelect;
    $[50] = listTitle;
    $[51] = locationInput;
    $[52] = sortByOptions;
    $[53] = sortInput;
    $[54] = t;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  return t22;
};